// KamSathi Admin — Dealer App Service
// Full read/write access to kamsathi-dealer Firebase

import { dealerDb } from './firebase'
import {
  collection, getDocs, doc, updateDoc, query,
  where, orderBy, getDoc, serverTimestamp, addDoc
} from 'firebase/firestore'

// ════════════════════════════════════════
// DEALERS
// ════════════════════════════════════════

export const getDealers = async (statusFilter = null) => {
  try {
    const q = statusFilter
      ? query(collection(dealerDb, 'dealers'), where('status', '==', statusFilter))
      : collection(dealerDb, 'dealers')
    const snap = await getDocs(q)
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
  } catch (error) {
    console.error('getDealers error:', error)
    return []
  }
}

export const getDealerStats = async () => {
  try {
    const snap = await getDocs(collection(dealerDb, 'dealers'))
    const dealers = snap.docs.map(d => d.data())
    return {
      total:    dealers.length,
      pending:  dealers.filter(d => d.status === 'pending').length,
      active:   dealers.filter(d => d.status === 'active').length,
      rejected: dealers.filter(d => d.status === 'rejected').length,
      suspended: dealers.filter(d => d.status === 'suspended').length,
      kycPending: dealers.filter(d => d.kycStatus === 'review').length,
    }
  } catch (error) {
    return { total: 0, pending: 0, active: 0, rejected: 0, suspended: 0, kycPending: 0 }
  }
}

export const approveDealer = async (dealerId, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'dealers', dealerId), {
      status: 'active',
      approvedAt: serverTimestamp(),
      approvedBy: adminEmail,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const rejectDealer = async (dealerId, reason, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'dealers', dealerId), {
      status: 'rejected',
      rejectReason: reason,
      rejectedAt: serverTimestamp(),
      rejectedBy: adminEmail,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const suspendDealer = async (dealerId, reason, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'dealers', dealerId), {
      status: 'suspended',
      suspendReason: reason,
      suspendedAt: serverTimestamp(),
      suspendedBy: adminEmail,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const approveKYC = async (dealerId, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'dealers', dealerId), {
      kycStatus: 'approved',
      kycApprovedAt: serverTimestamp(),
      kycApprovedBy: adminEmail,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const rejectKYC = async (dealerId, reason, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'dealers', dealerId), {
      kycStatus: 'rejected',
      kycRejectReason: reason,
      kycRejectedAt: serverTimestamp(),
      kycRejectedBy: adminEmail,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ════════════════════════════════════════
// PRODUCTS
// ════════════════════════════════════════

export const getDealerProducts = async (statusFilter = null) => {
  try {
    const q = statusFilter
      ? query(collection(dealerDb, 'products'), where('status', '==', statusFilter))
      : collection(dealerDb, 'products')
    const snap = await getDocs(q)
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
  } catch (error) {
    console.error('getDealerProducts error:', error)
    return []
  }
}

export const getDealerProductStats = async () => {
  try {
    const snap = await getDocs(collection(dealerDb, 'products'))
    const products = snap.docs.map(d => d.data())
    return {
      total:          products.length,
      pending_review: products.filter(p => p.status === 'pending_review').length,
      active:         products.filter(p => p.status === 'active').length,
      rejected:       products.filter(p => p.status === 'rejected').length,
      inactive:       products.filter(p => p.status === 'inactive').length,
    }
  } catch (error) {
    return { total: 0, pending_review: 0, active: 0, rejected: 0, inactive: 0 }
  }
}

export const approveProduct = async (productId, tier, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'products', productId), {
      status:      'active',
      tier:        tier,
      approvedAt:  serverTimestamp(),
      approvedBy:  adminEmail,
      updatedAt:   serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const rejectProduct = async (productId, reason, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'products', productId), {
      status:          'rejected',
      rejectionReason: reason,
      rejectedAt:      serverTimestamp(),
      rejectedBy:      adminEmail,
      updatedAt:       serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const changeTier = async (productId, newTier, reason, adminEmail) => {
  try {
    const productRef = doc(dealerDb, 'products', productId)
    const snap = await getDoc(productRef)
    const oldTier = snap.data()?.tier || 'none'
    await updateDoc(productRef, {
      tier:            newTier,
      tierChangedFrom: oldTier,
      tierChangeReason: reason,
      tierChangedAt:   serverTimestamp(),
      tierChangedBy:   adminEmail,
      updatedAt:       serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

export const toggleFeatured = async (productId, isFeatured, adminEmail) => {
  try {
    await updateDoc(doc(dealerDb, 'products', productId), {
      isFeatured,
      featuredUpdatedBy: adminEmail,
      updatedAt: serverTimestamp(),
    })
    return { success: true }
  } catch (error) {
    return { success: false, error: error.message }
  }
}

// ════════════════════════════════════════
// ORDERS
// ════════════════════════════════════════

export const getDealerOrders = async (statusFilter = null) => {
  try {
    const q = statusFilter
      ? query(collection(dealerDb, 'orders'), where('status', '==', statusFilter))
      : collection(dealerDb, 'orders')
    const snap = await getDocs(q)
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
  } catch (error) {
    console.error('getDealerOrders error:', error)
    return []
  }
}

export const getDealerOrderStats = async () => {
  try {
    const snap = await getDocs(collection(dealerDb, 'orders'))
    const orders = snap.docs.map(d => d.data())
    const delivered = orders.filter(o => o.status === 'delivered')
    return {
      total:     orders.length,
      pending:   orders.filter(o => o.status === 'pending').length,
      accepted:  orders.filter(o => o.status === 'accepted').length,
      packed:    orders.filter(o => o.status === 'packed').length,
      delivered: delivered.length,
      rejected:  orders.filter(o => o.status === 'rejected').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
      totalRevenue: delivered.reduce((s, o) => s + (o.amount || 0), 0),
      totalCommission: delivered.reduce((s, o) =>
        s + Math.round((o.amount || 0) * (o.commissionRate || 2) / 100), 0),
    }
  } catch (error) {
    return { total: 0, pending: 0, accepted: 0, packed: 0, delivered: 0, rejected: 0, cancelled: 0, totalRevenue: 0, totalCommission: 0 }
  }
}

// ════════════════════════════════════════
// COMMISSION LEDGER
// ════════════════════════════════════════

export const getWeeklyCommissionSummary = async () => {
  try {
    const snap = await getDocs(collection(dealerDb, 'dealers'))
    const dealers = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000

    const ordersSnap = await getDocs(
      query(collection(dealerDb, 'orders'), where('status', '==', 'delivered'))
    )
    const orders = ordersSnap.docs.map(d => ({ id: d.id, ...d.data() }))

    return dealers.map(dealer => {
      const dealerOrders = orders.filter(o =>
        o.dealerId === dealer.id &&
        (o.createdAt?.seconds || 0) * 1000 > weekAgo
      )
      const weeklyRevenue = dealerOrders.reduce((s, o) => s + (o.amount || 0), 0)
      const weeklyCommission = dealerOrders.reduce((s, o) =>
        s + Math.round((o.amount || 0) * (o.commissionRate || 2) / 100), 0)
      return {
        dealerId:         dealer.id,
        shopName:         dealer.shopName,
        phone:            dealer.phone,
        city:             dealer.city,
        status:           dealer.status,
        weeklyOrders:     dealerOrders.length,
        weeklyRevenue,
        weeklyCommission,
        commissionDue:    dealer.commissionDue || weeklyCommission,
      }
    }).filter(d => d.weeklyOrders > 0 || d.commissionDue > 0)
  } catch (error) {
    console.error('getWeeklyCommissionSummary error:', error)
    return []
  }
}